/*

  Chat.ts

  Chat component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView } from "./types";
import { ts } from "./i18n";
import { ComponentFunc, m, VnodeChildren, VnodeDOM } from "./mithril";
import { escapeHtml, getInput, glyph, setInput } from "./util";
import { DialogButton } from "./components";
import { serverUrl } from "./requestWithoutAuth";

// Max number of chat messages per game
const MAX_CHAT_MESSAGES = 250;

interface IAttributes {
  view: IView;
}

const EMOTICONS = [
  { icon: ":-)", image: "/static/icontexto_emoticons_03.png" },
  { icon: ":-D", image: "/static/icontexto_emoticons_02.png" },
  { icon: ";-)", image: "/static/icontexto_emoticons_04.png" },
  { icon: ":-(", image: "/static/icontexto_emoticons_12.png" },
  { icon: ":-o", image: "/static/icontexto_emoticons_10.png" },
  { icon: ":-O", image: "/static/icontexto_emoticons_10.png" },
  { icon: ":-p", image: "/static/icontexto_emoticons_14.png" },
  { icon: ":-P", image: "/static/icontexto_emoticons_14.png" },
  { icon: "B-)", image: "/static/icontexto_emoticons_16.png" },
  { icon: ":)",  image: "/static/icontexto_emoticons_03.png" },
  { icon: ":D",  image: "/static/icontexto_emoticons_02.png" },
  { icon: ";)",  image: "/static/icontexto_emoticons_04.png" },
  { icon: ":(",  image: "/static/icontexto_emoticons_12.png" },
  { icon: ":o",  image: "/static/icontexto_emoticons_10.png" },
  { icon: ":O",  image: "/static/icontexto_emoticons_10.png" },
  { icon: ":p",  image: "/static/icontexto_emoticons_14.png" },
  { icon: ":P",  image: "/static/icontexto_emoticons_14.png" },
  { icon: "(y)", image: "/static/thumb-up.png" }
];

export const Chat: ComponentFunc<IAttributes> = (initialVnode) => {
  // The chat tab

  const { view } = initialVnode.attrs;
  const model = view.model;
  const game = model.game!;
  const state = model.state!;

  function decodeTimestamp(ts: string) {
    // Parse and split an ISO timestamp string, formatted as YYYY-MM-DD HH:MM:SS
    return {
      year: parseInt(ts.slice(0, 4)),
      month: parseInt(ts.slice(5, 7)),
      day: parseInt(ts.slice(8, 10)),
      hour: parseInt(ts.slice(11, 13)),
      minute: parseInt(ts.slice(14, 16)),
      second: parseInt(ts.slice(17, 19))
    };
  }

  function dateFromTimestamp(ts: string) {
    // Create a JavaScript millisecond-based representation of an ISO timestamp
    var dcTs = decodeTimestamp(ts);
    return Date.UTC(dcTs.year, dcTs.month - 1, dcTs.day,
      dcTs.hour, dcTs.minute, dcTs.second);
  }

  function timeDiff(dtFrom: number, dtTo: number) {
    // Return the difference between two JavaScript time points, in seconds
    return Math.round((dtTo - dtFrom) / 1000.0);
  }

  let dtLastMsg: number | null = null;

  function makeTimestamp(ts: string, key: number): VnodeChildren {
    // Decode the ISO format timestamp we got from the server
    let dtTs = dateFromTimestamp(ts);
    let result: VnodeChildren = null;
    if (dtLastMsg === null || timeDiff(dtLastMsg, dtTs) >= 5 * 60) {
      // If 5 minutes or longer interval between messages,
      // insert a time
      const ONE_DAY = 24 * 60 * 60 * 1000; // 24 hours expressed in milliseconds
      const dtNow = new Date().getTime();
      let dtToday = dtNow - dtNow % ONE_DAY; // Start of today (00:00 UTC)
      let dtYesterday = dtToday - ONE_DAY; // Start of yesterday
      let strTs: string;
      if (dtTs < dtYesterday) {
        // Older than today or yesterday: Show full timestamp YYYY-MM-DD HH:MM
        strTs = ts.slice(0, -3);
      } else if (dtTs < dtToday) {
        // Yesterday
        strTs = "Í gær " + ts.slice(11, 16);
      } else {
        // Today
        strTs = ts.slice(11, 16);
      }
      result = m(".chat-ts", { key: key }, strTs);
    }
    dtLastMsg = dtTs;
    return result;
  }

  const player = game ? game.player : null;

  function replaceEmoticons(str: string): string {
    // Replace all emoticon shortcuts in the string str with a corresponding image URL
    for (const emoticon of EMOTICONS)
      if (str.indexOf(emoticon.icon) >= 0) {
        // The string contains the emoticon: prepare to replace all occurrences
        const imgUrl = serverUrl(state, emoticon.image);
        const img = `<img src='${imgUrl}' height='32' width='32'>`;
        // Re the following trick, see https://stackoverflow.com/questions/1144783/
        // replacing-all-occurrences-of-a-string-in-javascript
        str = str.split(emoticon.icon).join(img);
      }
    return str;
  }

  function chatMessages(): VnodeChildren {
    let r: VnodeChildren = [];
    if (game?.chatLoading || !game?.messages)
      return r;
    var key = 0;
    const userId = model.state?.netskraflUserId ?? "";
    for (const msg of game.messages) {
      let p = player ?? 0;
      if (msg.from_userid != userId)
        p = 1 - p;
      const mTs = makeTimestamp(msg.ts, key);
      if (mTs !== null) {
        r.push(mTs);
        key++;
      }
      let escMsg = escapeHtml(msg.msg);
      escMsg = replaceEmoticons(escMsg);
      r.push(m(".chat-msg" +
        (p === 0 ? ".left" : ".right") +
        (p === player ? ".local" : ".remote"),
        { key: key++ },
        m.trust(escMsg))
      );
    }
    return r;
  }

  function scrollChatToBottom(): void {
    // Scroll the last chat message into view
    const chatlist = document.querySelectorAll("#chat-area .chat-msg");
    if (!chatlist.length)
      return;
    const target: HTMLElement = chatlist[chatlist.length - 1] as HTMLElement;
    (target.parentNode as HTMLElement).scrollTop = target.offsetTop;
  }

  function focus(vnode: VnodeDOM) {
    // Put the focus on the DOM object associated with the vnode
    if (!view.isDialogShown()) {
      // Don't hijack the focus from a dialog overlay
      (vnode.dom as HTMLElement).focus();
    }
  }

  function sendMessage(): void {
    let msg = getInput("msg").trim();
    if (game && msg.length > 0) {
      game.sendMessage(msg);
      setInput("msg", "");
    }
  }

  const numMessages = game?.messages ? game.messages.length : 0;

  return {
    view: () => m(".chat-container",
      {
        style: "z-index: 6" // Appear on top of board on mobile
        // key: uuid
      },
      [
        m(".chat-area" + (game?.showClock() ? ".with-clock" : ""),
          {
            id: 'chat-area',
            // Make sure that we see the bottom-most chat message
            oncreate: scrollChatToBottom,
            onupdate: scrollChatToBottom
          },
          chatMessages()
        ),
        m(".chat-input",
          [
            m("input.chat-txt",
              {
                type: "text",
                id: "msg",
                name: "msg",
                maxlength: 254,
                disabled: (numMessages >= MAX_CHAT_MESSAGES),
                oncreate: (vnode) => { focus(vnode); },
                onupdate: (vnode) => { focus(vnode); },
                onkeypress: (ev: KeyboardEvent) => {
                  if (ev.key == "Enter") { sendMessage(); ev.preventDefault(); }
                }
              }
            ),
            m(DialogButton,
              {
                id: "chat-send",
                title: ts("Senda"),
                onclick: (ev: Event) => { sendMessage(); ev.preventDefault(); }
              },
              glyph("chat"),
            )
          ]
        )
      ]
    )
  }
};
